import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../config/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, ReferenceArea, ComposedChart, Area
} from "recharts";
import {
  ArrowLeft, RefreshCw, TrendingUp, TrendingDown, Activity,
  Droplets, Gauge, Clock, Target, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import EarlyWarningsCard from "../components/EarlyWarningsCard";
import MultiBenchmarkChart from "../components/MultiBenchmarkChart";
import { wmCellExtreme, wmCurrentBadge } from "../components/wmExtremes";

// Pseudo-ticker for the cap-weighted overall-market composite that the
// backend (`shared.py::_fetch_blended_market_history`) blends from
// SPY 58.5% + QQQ 29.2% + IWM 8.2% + DIA 4.1%.
const MARKET_TICKER = "MKTAVG";

const MarketIndicatorsPage = () => {
  const [chartData, setChartData] = useState([]);
  const [priceTrendData, setPriceTrendData] = useState([]);
  const [priceTrendZones, setPriceTrendZones] = useState({});
  const [priceTrendCurrent, setPriceTrendCurrent] = useState({});
  const [priceTrendRegression, setPriceTrendRegression] = useState({});
  const [priceTrendSignals, setPriceTrendSignals] = useState({});
  const [loading, setLoading] = useState(true);
  const [priceTrendLoading, setPriceTrendLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [currentValues, setCurrentValues] = useState({
    mfi: null,
    rsi: null,
    vix: null,
    market: null,
    spy: null,
    qqq: null,
    dia: null,
    iwm: null
  });
  const [timeframe, setTimeframe] = useState('1d');
  const [wmReturns, setWmReturns] = useState(null);

  // Weighted-market returns table (own cadence — backend caches 5 min)
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      axios.get(`${API}/market/weighted-market-returns`, { timeout: 45000 })
        .then(res => { if (!cancelled && res.data?.rows?.length) setWmReturns(res.data); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Fetch helper: single attempt with reasonable timeout for normal use,
  // retry only on actual network failures (not empty data)
  const fetchData = async (url, params, timeout = 25000) => {
    try {
      const response = await axios.get(url, { params, timeout });
      return response;
    } catch (err) {
      // Single retry on network failure only
      if (err.code === 'ECONNABORTED' || !err.response) {
        console.log(`Retrying ${url} after network failure...`);
        await new Promise(r => setTimeout(r, 2000));
        return await axios.get(url, { params, timeout });
      }
      throw err;
    }
  };

  const fetchIndicatorData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchData(`${API}/market/indicators-history`, { timeframe });
      
      if (response.data && response.data.chart_data) {
        setChartData(response.data.chart_data);
        setCurrentValues(response.data.current_values || currentValues);
        setLastUpdate(new Date());
      }
    } catch (err) {
      console.error("Error fetching indicator data:", err);
      setError("Failed to load indicator data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [timeframe]);

  const fetchPriceTrendData = useCallback(async () => {
    setPriceTrendLoading(true);
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await fetchData(`${API}/market/price-trend`, { timeframe }, 45000);
        
        if (response.data && response.data.chart_data) {
          setPriceTrendData(response.data.chart_data);
          setPriceTrendZones(response.data.zones || {});
          setPriceTrendCurrent(response.data.current || {});
          setPriceTrendRegression(response.data.regression || {});
          setPriceTrendSignals(response.data.signals || {});
          setPriceTrendLoading(false);
          return;
        }
      } catch (err) {
        console.error(`Price trend fetch attempt ${attempt + 1} failed:`, err);
        if (attempt === 0) await new Promise(r => setTimeout(r, 3000));
      }
    }
    setPriceTrendLoading(false);
  }, [timeframe]);

  useEffect(() => {
    // Serialize API calls to prevent load shedding rejections
    const init = async () => {
      await fetchIndicatorData();
      await fetchPriceTrendData();
    };
    init();
    
    const interval = setInterval(async () => {
      await fetchIndicatorData();
      await fetchPriceTrendData();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchIndicatorData, fetchPriceTrendData]);

  const getSignalColor = (value, type) => {
    if (type === 'mfi') {
      if (value >= 80) return "#ef4444"; // Overbought - red
      if (value >= 70) return "#f97316"; // Approaching overbought
      if (value >= 60) return "#22c55e"; // Bullish - green
      if (value >= 40) return "#6b7280"; // Neutral - gray
      if (value >= 30) return "#f97316"; // Approaching oversold
      return "#22c55e"; // Oversold - green (buy signal)
    } else {
      if (value >= 70) return "#ef4444"; // Overbought
      if (value >= 60) return "#f97316"; // Approaching overbought
      if (value >= 40) return "#6b7280"; // Neutral
      if (value >= 30) return "#f97316"; // Approaching oversold
      return "#22c55e"; // Oversold
    }
  };

  const getSignalText = (value, type) => {
    if (type === 'mfi') {
      if (value >= 80) return "EXTREMELY OVERBOUGHT";
      if (value >= 70) return "OVERBOUGHT";
      if (value >= 60) return "BULLISH";
      if (value >= 40) return "NEUTRAL";
      if (value >= 30) return "BEARISH";
      if (value >= 20) return "OVERSOLD";
      return "EXTREMELY OVERSOLD";
    } else {
      if (value >= 70) return "OVERBOUGHT";
      if (value >= 60) return "BULLISH";
      if (value >= 40) return "NEUTRAL";
      if (value >= 30) return "BEARISH";
      return "OVERSOLD";
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-3 shadow-xl">
          <p className="text-gray-400 text-xs mb-2">{label}</p>
          <div className="space-y-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <span 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-gray-300">{entry.name}:</span>
                <span className="font-bold" style={{ color: entry.color }}>
                  {entry.value?.toFixed(2)}
                  {(entry.name === 'MFI' || entry.name === 'RSI') ? '' : '%'}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-200">
      {/* Header */}
      <header className="bg-[#1a1a2e] border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a 
                href="/" 
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                data-testid="back-to-overview"
               target="_blank" rel="noopener noreferrer">
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm">Back to Overview</span>
              </a>
              <div className="h-6 w-px bg-gray-700" />
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-400" />
                Market Indicators Chart
              </h1>
            </div>
            <div className="flex items-center gap-4">
              {lastUpdate && (
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {lastUpdate.toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={fetchIndicatorData}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 rounded text-sm transition-colors"
                data-testid="refresh-indicators"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Current Values Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {/* MFI Card */}
          <div className="bg-gradient-to-br from-green-900/40 to-[#1a1a2e] border border-green-700/50 rounded-lg p-4" data-testid="mfi-card">
            <div className="flex items-center gap-2 mb-2">
              <Droplets className="w-4 h-4 text-green-400" />
              <span className="text-xs text-green-400 font-medium">MARKET MFI</span>
            </div>
            <div className="text-2xl font-bold" style={{ color: currentValues.mfi ? getSignalColor(currentValues.mfi, 'mfi') : '#6b7280' }}>
              {currentValues.mfi?.toFixed(2) || '--'}
            </div>
            <div className="text-[10px] mt-1" style={{ color: currentValues.mfi ? getSignalColor(currentValues.mfi, 'mfi') : '#6b7280' }}>
              {currentValues.mfi ? getSignalText(currentValues.mfi, 'mfi') : 'Loading...'}
            </div>
          </div>

          {/* RSI Card */}
          <div className="bg-gradient-to-br from-red-900/40 to-[#1a1a2e] border border-red-700/50 rounded-lg p-4" data-testid="rsi-card">
            <div className="flex items-center gap-2 mb-2">
              <Gauge className="w-4 h-4 text-red-400" />
              <span className="text-xs text-red-400 font-medium">MARKET RSI</span>
            </div>
            <div className="text-2xl font-bold" style={{ color: currentValues.rsi ? getSignalColor(currentValues.rsi, 'rsi') : '#6b7280' }}>
              {currentValues.rsi?.toFixed(2) || '--'}
            </div>
            <div className="text-[10px] mt-1" style={{ color: currentValues.rsi ? getSignalColor(currentValues.rsi, 'rsi') : '#6b7280' }}>
              {currentValues.rsi ? getSignalText(currentValues.rsi, 'rsi') : 'Loading...'}
            </div>
          </div>

          {/* VIX Card */}
          <div className="bg-gradient-to-br from-orange-900/40 to-[#1a1a2e] border border-orange-700/50 rounded-lg p-4" data-testid="vix-card">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-orange-400" />
              <span className="text-xs text-orange-400 font-medium">VIX</span>
            </div>
            <div className={`text-2xl font-bold ${currentValues.vix >= 20 ? 'text-red-400' : currentValues.vix >= 15 ? 'text-yellow-400' : 'text-green-400'}`}>
              {currentValues.vix?.toFixed(2) || '--'}
            </div>
            <div className={`text-[10px] mt-1 ${currentValues.vix >= 30 ? 'text-red-400' : currentValues.vix >= 20 ? 'text-orange-400' : currentValues.vix >= 15 ? 'text-yellow-400' : 'text-green-400'}`}>
              {currentValues.vix >= 30 ? 'EXTREME FEAR' : currentValues.vix >= 20 ? 'HIGH FEAR' : currentValues.vix >= 15 ? 'MODERATE' : currentValues.vix ? 'LOW FEAR' : 'Loading...'}
            </div>
          </div>

          {/* SPY Card */}
          <div className="bg-[#111] border border-gray-800 rounded-lg p-4" data-testid="spy-card">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-xs text-gray-400 font-medium">S&P 500</span>
            </div>
            <div className={`text-xl font-bold ${currentValues.spy >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {currentValues.spy !== null ? `${currentValues.spy >= 0 ? '+' : ''}${currentValues.spy?.toFixed(2)}%` : '--'}
            </div>
            <div className="text-[10px] text-gray-500 mt-1">^GSPC · {timeframe.toUpperCase()} change</div>
          </div>

          {/* QQQ Card */}
          <div className="bg-[#111] border border-gray-800 rounded-lg p-4" data-testid="qqq-card">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="text-xs text-gray-400 font-medium">NASDAQ 100</span>
            </div>
            <div className={`text-xl font-bold ${currentValues.qqq >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {currentValues.qqq !== null ? `${currentValues.qqq >= 0 ? '+' : ''}${currentValues.qqq?.toFixed(2)}%` : '--'}
            </div>
            <div className="text-[10px] text-gray-500 mt-1">^NDX · {timeframe.toUpperCase()} change</div>
          </div>

          {/* DIA Card */}
          <div className="bg-[#111] border border-gray-800 rounded-lg p-4" data-testid="dia-card">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-white" />
              <span className="text-xs text-gray-400 font-medium">DOW 30</span>
            </div>
            <div className={`text-xl font-bold ${currentValues.dia >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {currentValues.dia !== null ? `${currentValues.dia >= 0 ? '+' : ''}${currentValues.dia?.toFixed(2)}%` : '--'}
            </div>
            <div className="text-[10px] text-gray-500 mt-1">^DJI · {timeframe.toUpperCase()} change</div>
          </div>

          {/* IWM Card */}
          <div className="bg-[#111] border border-gray-800 rounded-lg p-4" data-testid="iwm-card">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-fuchsia-600" />
              <span className="text-xs text-gray-400 font-medium">RUSSELL 2000</span>
            </div>
            <div className={`text-xl font-bold ${currentValues.iwm >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {currentValues.iwm !== null ? `${currentValues.iwm >= 0 ? '+' : ''}${currentValues.iwm?.toFixed(2)}%` : '--'}
            </div>
            <div className="text-[10px] text-gray-500 mt-1">^RUT · {timeframe.toUpperCase()} change</div>
          </div>
        </div>

        {/* Market Index Card - Full Width */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-blue-900/40 via-purple-900/40 to-pink-900/40 border border-purple-500/50 rounded-lg p-4" data-testid="market-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-purple-400" />
                <div>
                  <span className="text-sm text-purple-300 font-medium">WEIGHTED MARKET INDEX</span>
                  <div className="text-[10px] text-gray-500">Matches Overview "US Market" · S&P 500 52.3% | NASDAQ 100 29.0% | Dow 30 15.5% | Russell 2000 3.1%</div>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-3xl font-bold ${currentValues.market >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {currentValues.market !== null ? `${currentValues.market >= 0 ? '+' : ''}${currentValues.market?.toFixed(2)}%` : '--'}
                </div>
                <div className="text-[10px] text-gray-500">Cap-Weighted Average Change</div>
              </div>
            </div>
          </div>
        </div>

        {/* Overall-Market Early Warnings — Deterioration / Recovery / Breakout
            scoring on SPY, sourced from the same scoring engine that powers
            the Early Warnings page. Tells the user at a glance whether the
            broad market is rolling over, bouncing, or breaking out. */}
        <div className="mb-4" data-testid="market-early-warnings">
          <EarlyWarningsCard ticker={MARKET_TICKER} />
        </div>

        {/* Weighted Market Returns table — same style/periods as the Overview
            US Index Returns box, plus composite MFI/RSI point changes, VIX %
            change and VWAP premium per window. */}
        <div className="mb-4 bg-[#0d0d0d] border border-gray-800 rounded-lg px-3 py-3" data-testid="weighted-market-returns-box">
          <div className="flex items-center gap-2 mb-2 px-1 flex-wrap">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-emerald-400 uppercase tracking-wider font-semibold">Weighted Market Returns</span>
            <span className="text-[9px] text-gray-500">S&P 50.5 | NASDAQ 28 | DOW 15 | RUSSELL 3 · MFI/RSI = daily 14-period composite point change · VWAP = market vs window VWAP · shaded MFI/RSI cells = level was overbought (≥70) / oversold (≤30) at that lookback</span>
          </div>
          {!wmReturns ? (
            <div className="text-xs text-gray-500 py-4 text-center animate-pulse" data-testid="weighted-market-returns-loading">
              Loading weighted market table…
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[980px]" data-testid="weighted-market-returns-table">
                <thead>
                  <tr className="text-gray-500 border-b border-gray-800">
                    <th className="text-left py-1.5 px-2 font-medium">Metric</th>
                    {wmReturns.windows.map(w => (
                      <th key={w} className="text-center py-1.5 px-2 font-medium">{w}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {wmReturns.rows.map(row => (
                    <tr key={row.key} className="border-b border-gray-800/50 hover:bg-[#161628]">
                      <td className="py-1.5 px-2 whitespace-nowrap">
                        <span className="text-white font-semibold">{row.name}</span>
                        {row.current != null && (
                          <span className="text-gray-500 ml-1.5">{Number(row.current).toLocaleString()}</span>
                        )}
                        {(() => {
                          const b = wmCurrentBadge(row);
                          return b ? (
                            <span className={`ml-1.5 px-1.5 py-0.5 rounded border text-[8px] font-bold align-middle ${b.cls}`}
                                  data-testid={`wm-extreme-badge-${row.key}`}>{b.text}</span>
                          ) : null;
                        })()}
                        <span className="text-gray-600 ml-1.5 text-[9px]">{row.note}</span>
                      </td>
                      {wmReturns.windows.map(w => {
                        const v = row.values?.[w];
                        const ex = wmCellExtreme(row, w);
                        return (
                          <td key={w} className="text-center py-1.5 px-2" data-testid={`wmret-${row.key}-${w}`}
                              title={ex ? `${row.name} level ${w} ago: ${ex.lvl} (${ex.label})` : undefined}>
                            {v == null ? (
                              <span className="text-gray-600">—</span>
                            ) : (
                              <span className={`font-medium px-1.5 py-0.5 rounded ${v >= 0 ? 'text-green-400' : 'text-red-400'} ${ex ? ex.cls : ''}`}
                                    data-extreme={ex ? ex.label : undefined}>
                                {v >= 0 ? '+' : ''}{Number(v).toFixed(2)}%
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Overall-Market Multi-Benchmark Chart — SPY plotted vs DIA / QQQ /
            IWM + its sector & subsector ETFs, with every overlay (EMAs,
            SMAs, VWAP, regression, ATR alert/stop, S/R) inherited from the
            same chart used on the Stock Analysis page. Timeframe binds to
            the page-level selector so 1D/5D/.../5Y all flow through. */}
        <div className="mb-4 bg-[#111] border border-gray-800 rounded-lg p-2" data-testid="market-multi-benchmark">
          <MultiBenchmarkChart ticker={MARKET_TICKER} timeframe={timeframe} canonValues={currentValues} />
        </div>

        {/* Timeframe Selector */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">MFI, RSI & Index Performance</h2>
          <div className="flex items-center gap-1 bg-[#111] rounded-lg p-1">
            {[
              { key: '1d',   label: '1D'   },
              { key: '5d',   label: '5D'   },
              { key: '10d',  label: '10D'  },
              { key: '20d',  label: '20D'  },
              { key: '50d',  label: '50D'  },
              { key: '100d', label: '100D' },
              { key: '200d', label: '200D' },
              { key: '1y',   label: '1Y'   },
              { key: '3y',   label: '3Y'   },
              { key: '5y',   label: '5Y'   }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setTimeframe(key)}
                className={`px-2 py-1.5 text-xs font-medium rounded transition-colors ${
                  timeframe === key
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
                data-testid={`timeframe-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Main Chart */}
        <div className="bg-[#111] border border-gray-800 rounded-lg p-4" data-testid="indicators-chart">
          {loading && chartData.length === 0 ? (
            <div className="h-[500px] p-4">
              <div className="animate-pulse bg-gray-800/60 rounded h-4 w-1/4 mb-4" />
              <div className="flex items-end gap-1 h-[85%]">
                {Array.from({length:40}).map((_,i) => (
                  <div key={i} className="animate-pulse bg-gray-800/60 rounded-t flex-1" style={{height: `${10+Math.random()*75}%`}} />
                ))}
              </div>
            </div>
          ) : error ? (
            <div className="h-[500px] flex items-center justify-center">
              <div className="text-center">
                <p className="text-red-400 mb-3">{error}</p>
                <button
                  onClick={fetchIndicatorData}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded text-sm"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={500}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis 
                  dataKey="time" 
                  stroke="#666"
                  tick={{ fill: '#888', fontSize: 10 }}
                  tickLine={{ stroke: '#444' }}
                />
                <YAxis 
                  yAxisId="indicators"
                  domain={[0, 100]}
                  stroke="#666"
                  tick={{ fill: '#888', fontSize: 10 }}
                  tickLine={{ stroke: '#444' }}
                  label={{ value: 'MFI / RSI', angle: -90, position: 'insideLeft', fill: '#888', fontSize: 12 }}
                />
                <YAxis 
                  yAxisId="performance"
                  orientation="right"
                  stroke="#666"
                  tick={{ fill: '#888', fontSize: 10 }}
                  tickLine={{ stroke: '#444' }}
                  label={{ value: 'Index Change %', angle: 90, position: 'insideRight', fill: '#888', fontSize: 12 }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="circle"
                />
                
                {/* Reference lines for overbought/oversold */}
                <ReferenceLine yAxisId="indicators" y={70} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'Overbought (70)', fill: '#ef4444', fontSize: 10, position: 'right' }} />
                <ReferenceLine yAxisId="indicators" y={30} stroke="#22c55e" strokeDasharray="5 5" label={{ value: 'Oversold (30)', fill: '#22c55e', fontSize: 10, position: 'right' }} />
                <ReferenceLine yAxisId="indicators" y={50} stroke="#6b7280" strokeDasharray="3 3" />
                <ReferenceLine yAxisId="performance" y={0} stroke="#6b7280" strokeDasharray="3 3" />
                
                {/* MFI Line */}
                <Line
                  yAxisId="indicators"
                  type="monotone"
                  dataKey="mfi"
                  name="MFI"
                  stroke="#22c55e"
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 6, fill: '#22c55e' }}
                />
                
                {/* RSI Line */}
                <Line
                  yAxisId="indicators"
                  type="monotone"
                  dataKey="rsi"
                  name="RSI"
                  stroke="#ef4444"
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 6, fill: '#ef4444' }}
                />
                
                {/* SPY Performance */}
                <Line
                  yAxisId="performance"
                  type="monotone"
                  dataKey="spy"
                  name="S&P 500"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5, fill: '#3b82f6' }}
                />
                
                {/* QQQ Performance */}
                <Line
                  yAxisId="performance"
                  type="monotone"
                  dataKey="qqq"
                  name="NASDAQ 100"
                  stroke="#eab308"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5, fill: '#eab308' }}
                />
                
                {/* DIA Performance */}
                <Line
                  yAxisId="performance"
                  type="monotone"
                  dataKey="dia"
                  name="DOW 30"
                  stroke="#ffffff"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5, fill: '#ffffff' }}
                />
                
                {/* IWM Performance */}
                <Line
                  yAxisId="performance"
                  type="monotone"
                  dataKey="iwm"
                  name="RUSSELL 2000"
                  stroke="#c026d3"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5, fill: '#c026d3' }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Second Chart - MFI, RSI & Market */}
        <div className="mt-6">
          <h2 className="text-lg font-semibold text-white mb-4">MFI, RSI & Weighted Market Index</h2>
          <div className="bg-[#111] border border-gray-800 rounded-lg p-4" data-testid="indicators-only-chart">
            {loading && chartData.length === 0 ? (
              <div className="h-[300px] p-4">
                <div className="flex items-end gap-1 h-full">
                  {Array.from({length:30}).map((_,i) => (
                    <div key={i} className="animate-pulse bg-gray-800/60 rounded-t flex-1" style={{height: `${15+Math.random()*65}%`}} />
                  ))}
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={chartData} margin={{ top: 20, right: 60, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis 
                    dataKey="time" 
                    stroke="#666"
                    tick={{ fill: '#888', fontSize: 10 }}
                    tickLine={{ stroke: '#444' }}
                  />
                  <YAxis 
                    yAxisId="indicators"
                    domain={[0, 100]}
                    stroke="#666"
                    tick={{ fill: '#888', fontSize: 10 }}
                    tickLine={{ stroke: '#444' }}
                    label={{ value: 'MFI / RSI (0-100)', angle: -90, position: 'insideLeft', fill: '#888', fontSize: 10 }}
                  />
                  <YAxis 
                    yAxisId="market"
                    orientation="right"
                    stroke="#666"
                    tick={{ fill: '#888', fontSize: 10 }}
                    tickLine={{ stroke: '#444' }}
                    tickFormatter={(value) => `${value}%`}
                    label={{ value: 'Market %', angle: 90, position: 'insideRight', fill: '#a855f7', fontSize: 10 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    wrapperStyle={{ paddingTop: '10px' }}
                    iconType="circle"
                  />
                  
                  {/* Reference lines for indicators */}
                  <ReferenceLine yAxisId="indicators" y={70} stroke="#f97316" strokeDasharray="5 5" label={{ value: 'OB (70)', fill: '#f97316', fontSize: 9, position: 'left' }} />
                  <ReferenceLine yAxisId="indicators" y={50} stroke="#6b7280" strokeDasharray="3 3" />
                  <ReferenceLine yAxisId="indicators" y={30} stroke="#84cc16" strokeDasharray="5 5" label={{ value: 'OS (30)', fill: '#84cc16', fontSize: 9, position: 'left' }} />
                  <ReferenceLine yAxisId="market" y={0} stroke="#a855f7" strokeDasharray="3 3" />
                  
                  {/* MFI Line */}
                  <Line
                    yAxisId="indicators"
                    type="monotone"
                    dataKey="mfi"
                    name="MFI"
                    stroke="#22c55e"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 6, fill: '#22c55e' }}
                  />
                  
                  {/* RSI Line */}
                  <Line
                    yAxisId="indicators"
                    type="monotone"
                    dataKey="rsi"
                    name="RSI"
                    stroke="#ef4444"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 6, fill: '#ef4444' }}
                  />
                  
                  {/* Weighted Market Index Line */}
                  <Line
                    yAxisId="market"
                    type="monotone"
                    dataKey="market"
                    name="Market Index"
                    stroke="#a855f7"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 6, fill: '#a855f7' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* VIX & Market Chart */}
        <div className="mt-6">
          <h2 className="text-lg font-semibold text-white mb-4">VIX & Weighted Market Index</h2>
          <div className="bg-[#111] border border-gray-800 rounded-lg p-4" data-testid="vix-chart">
            {loading && chartData.length === 0 ? (
              <div className="h-[300px] p-4">
                <div className="flex items-end gap-1 h-full">
                  {Array.from({length:30}).map((_,i) => (
                    <div key={i} className="animate-pulse bg-gray-800/60 rounded-t flex-1" style={{height: `${15+Math.random()*65}%`}} />
                  ))}
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={chartData} margin={{ top: 20, right: 60, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis 
                    dataKey="time" 
                    stroke="#666"
                    tick={{ fill: '#888', fontSize: 10 }}
                    tickLine={{ stroke: '#444' }}
                  />
                  <YAxis 
                    yAxisId="vix"
                    domain={['auto', 'auto']}
                    stroke="#666"
                    tick={{ fill: '#888', fontSize: 10 }}
                    tickLine={{ stroke: '#444' }}
                    label={{ value: 'VIX Level', angle: -90, position: 'insideLeft', fill: '#f97316', fontSize: 10 }}
                  />
                  <YAxis 
                    yAxisId="market"
                    orientation="right"
                    stroke="#666"
                    tick={{ fill: '#888', fontSize: 10 }}
                    tickLine={{ stroke: '#444' }}
                    tickFormatter={(value) => `${value}%`}
                    label={{ value: 'Market %', angle: 90, position: 'insideRight', fill: '#a855f7', fontSize: 10 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    wrapperStyle={{ paddingTop: '10px' }}
                    iconType="circle"
                  />
                  
                  {/* Reference lines for VIX levels */}
                  <ReferenceLine yAxisId="vix" y={30} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'Fear (30)', fill: '#ef4444', fontSize: 9, position: 'left' }} />
                  <ReferenceLine yAxisId="vix" y={20} stroke="#f97316" strokeDasharray="5 5" label={{ value: 'High (20)', fill: '#f97316', fontSize: 9, position: 'left' }} />
                  <ReferenceLine yAxisId="vix" y={15} stroke="#eab308" strokeDasharray="5 5" />
                  <ReferenceLine yAxisId="market" y={0} stroke="#a855f7" strokeDasharray="3 3" />
                  
                  {/* VIX Line */}
                  <Line
                    yAxisId="vix"
                    type="monotone"
                    dataKey="vix"
                    name="VIX"
                    stroke="#f97316"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 6, fill: '#f97316' }}
                  />
                  
                  {/* Weighted Market Index Line */}
                  <Line
                    yAxisId="market"
                    type="monotone"
                    dataKey="market"
                    name="Market Index"
                    stroke="#a855f7"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 6, fill: '#a855f7' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Cap-Weighted Market Index Price Trend Chart */}
        <div className="mt-6 bg-[#111] border border-gray-800 rounded-lg p-4" data-testid="price-trend-chart">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5 text-purple-400" />
              <div>
                <h3 className="text-lg font-semibold text-white">Cap-Weighted Market Index</h3>
                <p className="text-xs text-gray-500">SPY 58.5% | QQQ 29.2% | IWM 8.2% | DIA 4.1% • Entry/Exit zones based on support/resistance</p>
              </div>
            </div>
            {priceTrendRegression.trend && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                priceTrendRegression.trend === 'BULLISH' ? 'bg-green-900/30 text-green-400' :
                priceTrendRegression.trend === 'BEARISH' ? 'bg-red-900/30 text-red-400' :
                'bg-yellow-900/30 text-yellow-400'
              }`}>
                {priceTrendRegression.trend === 'BULLISH' ? <TrendingUp className="w-4 h-4" /> :
                 priceTrendRegression.trend === 'BEARISH' ? <TrendingDown className="w-4 h-4" /> :
                 <Activity className="w-4 h-4" />}
                <span className="text-sm font-semibold">{priceTrendRegression.trend}</span>
                <span className="text-xs opacity-70">
                  ({priceTrendRegression.slope_pct >= 0 ? '+' : ''}{priceTrendRegression.slope_pct?.toFixed(2)}%)
                </span>
              </div>
            )}
          </div>

          {/* Signal Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
            {/* Current Price */}
            <div className={`bg-black/30 border rounded-lg p-3 ${
              priceTrendCurrent.change_pct >= 0 ? 'border-green-900/50' : 'border-red-900/50'
            }`}>
              <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                Index Price
                {priceTrendCurrent.change_pct >= 0 ? 
                  <ArrowUpRight className="w-3 h-3 text-green-400" /> : 
                  <ArrowDownRight className="w-3 h-3 text-red-400" />
                }
              </div>
              <div className={`text-lg font-bold ${priceTrendCurrent.change_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                ${priceTrendCurrent.price?.toFixed(2) || '--'}
              </div>
              <div className="text-xs text-gray-500">
                {priceTrendCurrent.change_pct >= 0 ? '+' : ''}{priceTrendCurrent.change_pct?.toFixed(2) || '0.00'}%
              </div>
            </div>
            
            {/* Entry Zone */}
            <div className={`bg-black/30 border rounded-lg p-3 ${
              priceTrendSignals.near_support ? 'border-green-500 bg-green-900/10' : 'border-gray-800'
            }`}>
              <div className="text-xs text-green-500 mb-1 flex items-center gap-1">
                <ArrowUpRight className="w-3 h-3" />
                Entry Zone
              </div>
              <div className="text-sm font-bold text-green-400">
                ${priceTrendZones.entry_zone?.low?.toFixed(2)} - ${priceTrendZones.entry_zone?.high?.toFixed(2)}
              </div>
              {priceTrendSignals.near_support && (
                <div className="text-xs text-green-400 mt-1 font-medium animate-pulse">⬆ Near Entry!</div>
              )}
            </div>
            
            {/* Exit Zone */}
            <div className={`bg-black/30 border rounded-lg p-3 ${
              priceTrendSignals.near_resistance ? 'border-red-500 bg-red-900/10' : 'border-gray-800'
            }`}>
              <div className="text-xs text-red-500 mb-1 flex items-center gap-1">
                <ArrowDownRight className="w-3 h-3" />
                Exit Zone
              </div>
              <div className="text-sm font-bold text-red-400">
                ${priceTrendZones.exit_zone?.low?.toFixed(2)} - ${priceTrendZones.exit_zone?.high?.toFixed(2)}
              </div>
              {priceTrendSignals.near_resistance && (
                <div className="text-xs text-red-400 mt-1 font-medium animate-pulse">⬇ Near Exit!</div>
              )}
            </div>
            
            {/* Support/Resistance */}
            <div className="bg-black/30 border border-gray-800 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Key Levels</div>
              <div className="flex flex-col gap-1">
                <div className="flex justify-between text-xs">
                  <span className="text-red-400">Resist:</span>
                  <span className="text-white font-medium">${priceTrendZones.resistance?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-green-400">Support:</span>
                  <span className="text-white font-medium">${priceTrendZones.support?.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            {/* R-Squared (Trend Strength) */}
            <div className="bg-black/30 border border-gray-800 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Trend Strength</div>
              <div className={`text-lg font-bold ${
                priceTrendRegression.r_squared >= 0.7 ? 'text-green-400' :
                priceTrendRegression.r_squared >= 0.5 ? 'text-yellow-400' : 'text-gray-400'
              }`}>
                {((priceTrendRegression.r_squared || 0) * 100).toFixed(0)}%
              </div>
              <div className="text-xs text-gray-500">R² Correlation</div>
              <div className={`text-[10px] mt-1 ${
                priceTrendRegression.r_squared >= 0.7 ? 'text-green-500' :
                priceTrendRegression.r_squared >= 0.5 ? 'text-yellow-500' : 'text-gray-500'
              }`}>
                {priceTrendRegression.r_squared >= 0.7 ? 'Strong trend — price closely follows the regression line' :
                 priceTrendRegression.r_squared >= 0.5 ? 'Moderate trend — price loosely follows the regression line' :
                 priceTrendRegression.r_squared >= 0.3 ? 'Weak trend — price deviates significantly from the regression line' :
                 'No clear trend — price movement is mostly random'}
              </div>
            </div>
          </div>

          {/* Price Trend Chart */}
          {priceTrendLoading && priceTrendData.length === 0 ? (
            <div className="h-[350px] p-4">
              <div className="animate-pulse bg-gray-800/60 rounded h-4 w-1/4 mb-4" />
              <div className="flex items-end gap-1 h-[85%]">
                {Array.from({length:35}).map((_,i) => (
                  <div key={i} className="animate-pulse bg-gray-800/60 rounded-t flex-1" style={{height: `${10+Math.random()*75}%`}} />
                ))}
              </div>
            </div>
          ) : priceTrendData.length === 0 ? (
            <div className="h-[350px] flex items-center justify-center">
              <p className="text-gray-500">No price trend data available</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={priceTrendData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis 
                  dataKey="time" 
                  stroke="#64748b" 
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  stroke="#64748b" 
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  domain={['auto', 'auto']}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1a1a2e', 
                    border: '1px solid #374151', 
                    borderRadius: '8px',
                    padding: '10px'
                  }}
                  labelStyle={{ color: '#9ca3af', marginBottom: '5px' }}
                  formatter={(value, name) => {
                    if (name === 'Price') return [`$${value?.toFixed(2)}`, name];
                    if (name === 'Regression') return [`$${value?.toFixed(2)}`, 'Trend Line'];
                    if (name === 'SMA 20') return [`$${value?.toFixed(2)}`, name];
                    if (name === 'SMA 50') return [`$${value?.toFixed(2)}`, name];
                    return [value, name];
                  }}
                />
                
                {/* Entry Zone (Green shaded area) */}
                {priceTrendZones.entry_zone && (
                  <ReferenceArea 
                    y1={priceTrendZones.entry_zone.low} 
                    y2={priceTrendZones.entry_zone.high} 
                    fill="#22c55e" 
                    fillOpacity={0.1}
                    stroke="#22c55e"
                    strokeDasharray="3 3"
                    strokeOpacity={0.5}
                  />
                )}
                
                {/* Exit Zone (Red shaded area) */}
                {priceTrendZones.exit_zone && (
                  <ReferenceArea 
                    y1={priceTrendZones.exit_zone.low} 
                    y2={priceTrendZones.exit_zone.high} 
                    fill="#ef4444" 
                    fillOpacity={0.1}
                    stroke="#ef4444"
                    strokeDasharray="3 3"
                    strokeOpacity={0.5}
                  />
                )}
                
                {/* Support Line */}
                {priceTrendZones.support && (
                  <ReferenceLine 
                    y={priceTrendZones.support} 
                    stroke="#22c55e" 
                    strokeDasharray="5 5" 
                    strokeWidth={2}
                  />
                )}
                
                {/* Resistance Line */}
                {priceTrendZones.resistance && (
                  <ReferenceLine 
                    y={priceTrendZones.resistance} 
                    stroke="#ef4444" 
                    strokeDasharray="5 5" 
                    strokeWidth={2}
                  />
                )}
                
                {/* SMA 50 */}
                <Line 
                  type="monotone" 
                  dataKey="sma50" 
                  stroke="#06b6d4" 
                  strokeWidth={1.5} 
                  dot={false}
                  name="SMA 50"
                  strokeDasharray="3 3"
                />
                
                {/* SMA 20 */}
                <Line 
                  type="monotone" 
                  dataKey="sma20" 
                  stroke="#3b82f6" 
                  strokeWidth={1.5} 
                  dot={false}
                  name="SMA 20"
                />
                
                {/* Regression Line */}
                <Line 
                  type="monotone" 
                  dataKey="regression" 
                  stroke="#f59e0b" 
                  strokeWidth={2.5} 
                  dot={false}
                  name="Regression"
                  strokeDasharray="8 4"
                />
                
                {/* Price Line */}
                <Line 
                  type="monotone" 
                  dataKey="price" 
                  stroke="#a855f7" 
                  strokeWidth={2.5} 
                  dot={false}
                  name="Price"
                  activeDot={{ r: 6, fill: '#a855f7' }}
                />
                
                <Legend 
                  verticalAlign="top"
                  height={36}
                  formatter={(value) => <span style={{ color: '#94a3b8', fontSize: '11px' }}>{value}</span>}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
          
          {/* Legend / Key */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-4 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <div className="w-8 h-3 bg-green-500/20 border border-green-500 border-dashed rounded" />
              <span>Entry Zone</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-3 bg-red-500/20 border border-red-500 border-dashed rounded" />
              <span>Exit Zone</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 border-t-2 border-purple-500" />
              <span>Index Price</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 border-t-2 border-amber-500 border-dashed" />
              <span>Regression Trend</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 border-t-2 border-blue-500" />
              <span>SMA 20</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 border-t-2 border-cyan-500 border-dashed" />
              <span>SMA 50</span>
            </div>
          </div>
        </div>

        {/* Legend Explanation */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[#111] border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-green-400 mb-3 flex items-center gap-2">
              <Droplets className="w-4 h-4" />
              Money Flow Index (MFI)
            </h3>
            <p className="text-xs text-gray-400 mb-3">
              Volume-weighted RSI that measures buying and selling pressure using price and volume.
            </p>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-16 text-red-400">80+</span>
                <span className="text-gray-300">Extremely Overbought</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-16 text-orange-400">70-80</span>
                <span className="text-gray-300">Overbought - Caution</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-16 text-green-400">60-70</span>
                <span className="text-gray-300">Bullish</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-16 text-gray-400">40-60</span>
                <span className="text-gray-300">Neutral</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-16 text-green-400">20-40</span>
                <span className="text-gray-300">Oversold - Buy signal</span>
              </div>
            </div>
          </div>

          <div className="bg-[#111] border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-red-400 mb-3 flex items-center gap-2">
              <Gauge className="w-4 h-4" />
              Relative Strength Index (RSI)
            </h3>
            <p className="text-xs text-gray-400 mb-3">
              Momentum oscillator measuring the speed and magnitude of recent price changes.
            </p>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-16 text-red-400">70+</span>
                <span className="text-gray-300">Overbought - May be due for pullback</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-16 text-orange-400">60-70</span>
                <span className="text-gray-300">Bullish momentum</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-16 text-gray-400">40-60</span>
                <span className="text-gray-300">Neutral - No strong trend</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-16 text-orange-400">30-40</span>
                <span className="text-gray-300">Bearish momentum</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-16 text-green-400">Below 30</span>
                <span className="text-gray-300">Oversold - Potential bounce</span>
              </div>
            </div>
          </div>

          <div className="bg-[#111] border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-orange-400 mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4" />
              VIX Volatility Index
            </h3>
            <p className="text-xs text-gray-400 mb-3">
              Measures expected 30-day volatility of the S&P 500. Higher = more fear in markets.
            </p>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-16 text-red-400">30+</span>
                <span className="text-gray-300">Extreme Fear - High volatility</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-16 text-orange-400">20-30</span>
                <span className="text-gray-300">High Fear - Elevated risk</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-16 text-yellow-400">15-20</span>
                <span className="text-gray-300">Moderate - Normal range</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-16 text-green-400">12-15</span>
                <span className="text-gray-300">Low Fear - Complacent</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-16 text-green-400">Below 12</span>
                <span className="text-gray-300">Very Low - Extreme calm</span>
              </div>
            </div>
          </div>
        </div>

        {/* ETF Info */}
        <div className="mt-4 bg-[#111] border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-white mb-3">Underlying ETFs (Cap-Weighted)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-gray-400">SPY (58.5%)</span>
              <span className="text-gray-500">- S&P 500</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="text-gray-400">QQQ (29.2%)</span>
              <span className="text-gray-500">- NASDAQ 100</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-white border border-gray-600" />
              <span className="text-gray-400">DIA (4.1%)</span>
              <span className="text-gray-500">- DOW 30</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-fuchsia-600" />
              <span className="text-gray-400">IWM (8.2%)</span>
              <span className="text-gray-500">- Russell 2000</span>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
};

export default MarketIndicatorsPage;
